import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Award, BookOpen, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const LETTERS = ['A', 'B', 'C', 'D'];

/**
 * KindergartenDashboard
 * =====================
 * A stripped-down, kindergarten-friendly worksheet page for Medley (and the K pod).
 * Big text, emoji answers, one question at a time, celebrations on submit.
 * Reads from /api/school/daily-worksheet just like every other grade.
 */
const KindergartenDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/school/daily-worksheet', { headers: authHeaders() });
      setWs(r.data.worksheet);
      if (r.data.submission) {
        setResult(r.data.submission);
        setAnswers(r.data.submission.answers || {});
      } else if (r.data.draft?.answers) {
        setAnswers(r.data.draft.answers);
      }
    } catch {
      toast.error("We couldn't load today's lesson. Please try again.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = (qid, letter) => {
    setAnswers((a) => {
      const next = { ...a, [qid]: letter };
      try { localStorage.setItem(`saa_k_draft_${ws?.date}`, JSON.stringify(next)); } catch { /* quota */ }
      // Debounced server save
      clearTimeout(pick._t);
      pick._t = setTimeout(async () => {
        try { await api.post('/school/daily-worksheet/draft', { date: ws.date, answers: next }, { headers: authHeaders() }); } catch { /* offline is fine */ }
      }, 1500);
      return next;
    });
  };

  const submit = async () => {
    if (!ws) return;
    setSubmitting(true);
    try {
      const r = await api.post('/school/daily-worksheet/submit', { date: ws.date, answers }, { headers: authHeaders() });
      setResult(r.data.submission);
      toast.success('Great work, scholar! 🏆');
      try { localStorage.removeItem(`saa_k_draft_${ws?.date}`); } catch { /* noop */ }
    } catch { toast.error('Could not submit. Your answers are saved — try again in a moment.'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-b from-sky-400 to-sky-600 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-white" /></div>;
  }
  if (!ws) return null;
  const total = ws.questions?.length || 0;
  const answered = Object.keys(answers).length;
  const progress = total > 0 ? Math.round((answered / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-400 via-sky-500 to-blue-600 text-white pt-8 pb-24 px-4" data-testid="kindergarten-dashboard">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="bg-white text-slate-900 rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center gap-2 text-orange-500 font-black uppercase tracking-widest text-xs" data-testid="k-header-label">
            <Sparkles className="w-4 h-4" /> Scholar Athlete Academy · Kindergarten
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mt-2 leading-tight" data-testid="k-title">{ws.title}</h1>
          <p className="text-slate-600 mt-2 text-lg sm:text-xl leading-snug" data-testid="k-subject">{ws.subject} · {ws.weekday_label}</p>
        </div>

        {/* Mini-lesson (Coach reads out loud) */}
        <div className="mt-4 bg-yellow-100 text-slate-900 rounded-3xl p-6 shadow-xl border-4 border-yellow-400" data-testid="k-mini-lesson">
          <div className="flex items-center gap-2 text-yellow-700 font-black uppercase tracking-widest text-xs">
            <BookOpen className="w-4 h-4" /> Coach's Corner
          </div>
          <p className="mt-2 text-lg sm:text-xl leading-relaxed font-medium">{ws.mini_lesson}</p>
        </div>

        {/* Progress bar */}
        {!result && total > 0 && (
          <div className="mt-6 bg-white/20 backdrop-blur-sm rounded-full h-4 overflow-hidden">
            <div className="h-full bg-orange-400 transition-all duration-500" style={{ width: `${progress}%` }} data-testid="k-progress" />
          </div>
        )}
        {!result && (
          <div className="text-center text-white font-black mt-2 tracking-widest" data-testid="k-progress-label">
            {answered} / {total} answered
          </div>
        )}

        {/* Result banner */}
        {result && (
          <div className="mt-6 bg-emerald-400 text-slate-900 rounded-3xl p-6 shadow-xl border-4 border-emerald-600 text-center" data-testid="k-result-banner">
            <Award className="w-12 h-12 mx-auto text-emerald-800" />
            <div className="text-4xl font-black mt-2">{result.objective_score}%</div>
            <div className="text-lg font-bold mt-1">Great work, scholar! 🏆</div>
            <div className="text-sm mt-1 text-emerald-900">{result.objective_correct} of {result.objective_total} correct</div>
          </div>
        )}

        {/* Questions */}
        <div className="mt-6 space-y-4" data-testid="k-questions">
          {ws.questions?.map((q, i) => {
            const picked = answers[q.id];
            const gradedItem = result?.results?.find((r) => r.id === q.id);
            return (
              <div key={q.id} className="bg-white text-slate-900 rounded-3xl p-6 shadow-xl" data-testid={`k-q-${i}`}>
                <div className="text-xs uppercase font-black text-orange-500 tracking-widest">Question {i + 1}</div>
                <div className="text-xl sm:text-2xl font-black leading-tight mt-2">{q.prompt}</div>
                <div className="mt-4 space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const letter = LETTERS[oi];
                    const isPicked = picked === letter;
                    const isCorrect = gradedItem?.answer === letter;
                    const isWrongPick = gradedItem && isPicked && !gradedItem.correct;
                    let cls = 'border-slate-200 bg-slate-50 hover:bg-sky-50 hover:border-sky-400';
                    if (result) {
                      if (isCorrect) cls = 'border-emerald-500 bg-emerald-100';
                      else if (isWrongPick) cls = 'border-red-400 bg-red-50';
                      else cls = 'border-slate-200 bg-white';
                    } else if (isPicked) {
                      cls = 'border-orange-500 bg-orange-100';
                    }
                    return (
                      <button
                        key={oi}
                        onClick={() => !result && pick(q.id, letter)}
                        disabled={!!result}
                        className={`w-full text-left border-4 rounded-2xl px-4 py-4 text-lg sm:text-xl font-bold transition-all ${cls} disabled:cursor-default`}
                        data-testid={`k-q-${i}-opt-${oi}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {gradedItem?.explanation && (
                  <div className="mt-3 text-sm text-slate-700 italic border-l-4 border-emerald-400 pl-3" data-testid={`k-q-${i}-explanation`}>
                    <span className="font-black not-italic text-emerald-700">Why: </span>{gradedItem.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit */}
        {!result && (
          <button onClick={submit} disabled={submitting || answered < total}
                  className="mt-8 w-full bg-orange-500 hover:bg-orange-400 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-xl py-6 rounded-3xl shadow-2xl transition-all"
                  data-testid="k-submit-btn">
            {submitting ? 'Locking in...' : answered < total ? `Answer all ${total} to finish` : 'I\'m Done! 🏆'}
          </button>
        )}
      </div>
    </div>
  );
};

export default KindergartenDashboard;
